import React from "react";
import Tooltip from "./Tooltip";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";

type LabelItem = {
    id?: string | number;
    title?: string;
    description?: string | null;
    guid?: string;
    text_color?: string | null;
    background_color?: string | null;
    origin_app?: string | null;
};

type Props = {
    labels?: LabelItem[] | null;
    className?: string;
    emptyPlaceholder?: boolean;
};

export function getOriginAppLabel(origin: unknown, t: (key: string) => string): string {
    const slug = String(origin || "").trim().toLowerCase();

    if ( !slug ) {
        return "";
    }

    if ( slug === "alpc" ) {
        return process.env.REACT_APP_LOAD_COMMENT_ORIGIN_ALPC || t("load comment origin alpc");
    }

    if ( slug === "acm" ) {
        return process.env.REACT_APP_LOAD_COMMENT_ORIGIN_ACM || t("load comment origin acm");
    }

    return slug.toUpperCase();
}

const OrderLabel: React.FC<Props> = ({ labels, className, emptyPlaceholder = false }) => {
    const { t } = useTranslation();

    if ( !labels || isEmpty(labels) ) {
        return emptyPlaceholder ? <span className={ className }>-</span> : null;
    }

    return (
        <div
            className={ `order-labels-display ${ className || "" }`.trim() }
            style={ { display: 'flex', flexDirection: 'column', gap: '4px' } }
        >
            { labels.map((label, index) => {
                const title = label.title || "-";
                const description = label.description?.trim() || "";
                const originLabel = getOriginAppLabel(label.origin_app, t);
                const hasDescription = description.length > 0;
                const hasOrigin = originLabel.length > 0;
                const showTooltip = hasOrigin || hasDescription;

                let tooltipText = "";
                if ( hasOrigin && hasDescription ) {
                    tooltipText = `${ originLabel } : ${ description }`;
                } else if ( hasOrigin ) {
                    tooltipText = originLabel;
                } else {
                    tooltipText = description;
                }

                return (
                    <div
                        key={ label.id || label.guid || index }
                        className="order-labels-display__item"
                        style={ { display: 'inline-block' } }
                    >
                        { (() => {
                            const text = (label.text_color || '000000').toString().replace('#', '');
                            const background = (label.background_color || '3b82f6').toString().replace('#', '');
                            const badgeStyle: React.CSSProperties = {
                                backgroundColor: `#${background}`,
                                color: `#${text}`,
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: '6px',
                                padding: '2px 8px',
                                display: 'inline-block',
                                fontSize: '0.85rem',
                                lineHeight: '1.4',
                            };

                            const badge = <span style={ badgeStyle }>{ title }</span>;

                            if ( !showTooltip ) {
                                return badge;
                            }

                            return (
                                <Tooltip
                                    tooltip={
                                        <div
                                            className="order-labels-display__tooltip"
                                            style={ { maxWidth: '300px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } }
                                        >
                                            { tooltipText }
                                        </div>
                                    }
                                    position="top"
                                    className="order-labels-display__tooltip-wrapper"
                                >
                                    { badge }
                                </Tooltip>
                            );
                        })() }
                    </div>
                );
            }) }
        </div>
    );
};

export default React.memo(OrderLabel);
